import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { OrderStatusBadge, formatCurrency, formatDateTime } from '@/lib/utils-helpers';
import { Users, Truck, Store, ShoppingBag, DollarSign, TrendingUp, AlertCircle, Clock, Package, Bike, FileText, CheckCircle2, XCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState([]);
  const [onboardingAnalytics, setOnboardingAnalytics] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashRes, revRes, onboardRes] = await Promise.all([
          api.get('/admin/dashboard'),
          api.get('/admin/analytics/revenue').catch(() => ({ data: { daily: [] } })),
          api.get('/admin/vendors/onboarding-analytics').catch(() => ({ data: null }))
        ]);
        setData(dashRes.data);
        setRevenueData(revRes.data.daily || revRes.data.daily_revenue || []);
        setOnboardingAnalytics(onboardRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div><Skeleton className="h-8 w-48" /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Skeleton className="h-64" /><Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  const kpis = [
    { label: 'Total Orders', value: data?.total_orders || 0, icon: ShoppingBag, color: 'text-blue-600 bg-blue-50' },
    { label: 'Total Revenue', value: formatCurrency(data?.total_revenue), icon: DollarSign, color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Total Vendors', value: data?.total_vendors || 0, icon: Store, color: 'text-amber-600 bg-amber-50' },
    { label: 'Online Drivers', value: data?.online_drivers || 0, icon: Truck, color: 'text-purple-600 bg-purple-50' },
  ];

  // Use the recent revenue data for chart
  const chartData = revenueData.slice(-14).filter(d => d.revenue > 0 || d.orders > 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">Dashboard Overview</h1>
        <p className="text-muted-foreground mt-1">Welcome back. Here's what's happening on your platform.</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <Card key={kpi.label} className="kpi-card" data-testid={`admin-overview-${kpi.label.toLowerCase().replace(/\s+/g, '-')}-kpi`}>
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-muted-foreground">{kpi.label}</span>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${kpi.color}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                </div>
                <p className="text-2xl md:text-3xl font-semibold tabular-nums" data-font="display">{kpi.value}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts + Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Revenue Trend (Last 14 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 20% 90%)" strokeOpacity={0.6} />
                  <XAxis dataKey="date" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => v?.slice(5)} />
                  <YAxis fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `$${v}`} />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid hsl(214 20% 90%)', fontSize: '12px' }} formatter={v => [`$${Number(v).toFixed(2)}`, 'Revenue']} />
                  <Bar dataKey="revenue" fill="hsl(351 96% 31%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center py-12 text-muted-foreground">No revenue data available yet</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Platform Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <TrendingUp className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm">Service Fees Collected</span>
                </div>
                <span className="font-semibold tabular-nums" data-font="display">{formatCurrency(data?.total_service_fees)}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-amber-600" />
                  <span className="text-sm">Active Orders</span>
                </div>
                <span className="font-semibold tabular-nums" data-font="display">{data?.active_orders || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Users className="h-4 w-4 text-blue-600" />
                  <span className="text-sm">Total Users</span>
                </div>
                <span className="font-semibold tabular-nums" data-font="display">{data?.total_users || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Bike className="h-4 w-4 text-indigo-600" />
                  <span className="text-sm">Total Drivers</span>
                </div>
                <span className="font-semibold tabular-nums" data-font="display">{data?.total_drivers || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-4 w-4 text-rose-600" />
                  <span className="text-sm">Today's Orders</span>
                </div>
                <span className="font-semibold tabular-nums" data-font="display">{data?.today_orders || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Package className="h-4 w-4 text-teal-600" />
                  <span className="text-sm">Errands</span>
                </div>
                <span className="font-semibold tabular-nums" data-font="display">{data?.total_errands || 0}</span>

      {/* Vendor Onboarding Analytics */}
      {onboardingAnalytics && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium">Vendor Onboarding Analytics</CardTitle>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/admin/vendors/pending')}
              >
                View All Pending
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
              <div className="text-center p-3 bg-amber-50 rounded-lg">
                <p className="text-2xl font-bold text-amber-700" data-font="display">
                  {onboardingAnalytics.status_counts?.pending_review || 0}
                </p>
                <p className="text-xs text-amber-600 mt-1">Pending Review</p>
              </div>
              <div className="text-center p-3 bg-orange-50 rounded-lg">
                <p className="text-2xl font-bold text-orange-700" data-font="display">
                  {onboardingAnalytics.status_counts?.changes_requested || 0}
                </p>
                <p className="text-xs text-orange-600 mt-1">Changes Requested</p>
              </div>
              <div className="text-center p-3 bg-emerald-50 rounded-lg">
                <p className="text-2xl font-bold text-emerald-700" data-font="display">
                  {onboardingAnalytics.status_counts?.approved || 0}
                </p>
                <p className="text-xs text-emerald-600 mt-1">Approved</p>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-700" data-font="display">
                  {onboardingAnalytics.status_counts?.in_progress || 0}
                </p>
                <p className="text-xs text-blue-600 mt-1">In Progress</p>
              </div>
              <div className="text-center p-3 bg-rose-50 rounded-lg">
                <p className="text-2xl font-bold text-rose-700" data-font="display">
                  {onboardingAnalytics.status_counts?.rejected || 0}
                </p>
                <p className="text-xs text-rose-600 mt-1">Rejected</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
                <span className="text-muted-foreground">Completion Rate</span>
                <span className="font-semibold">{onboardingAnalytics.metrics?.completion_rate || 0}%</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
                <span className="text-muted-foreground">Approval Rate</span>
                <span className="font-semibold text-emerald-600">{onboardingAnalytics.metrics?.approval_rate || 0}%</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
                <span className="text-muted-foreground">Last 7 Days</span>
                <span className="font-semibold">{onboardingAnalytics.metrics?.recent_submissions_7d || 0} new</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
                <span className="text-muted-foreground">Avg. Time</span>
                <span className="font-semibold">{onboardingAnalytics.metrics?.avg_completion_hours || 0}h</span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t">
              <p className="text-xs text-muted-foreground mb-2">Step Distribution (Where vendors are stuck)</p>
              <div className="grid grid-cols-7 gap-1">
                {Object.entries(onboardingAnalytics.step_distribution || {}).map(([step, count]) => (
                  <div key={step} className="text-center">
                    <div className={`h-16 rounded flex items-end justify-center ${
                      count > 0 ? 'bg-primary/20' : 'bg-muted/30'
                    }`} style={{ height: `${Math.max(count * 10, 16)}px` }}>
                      <span className="text-xs font-medium pb-1">{count}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{step.replace('step_', '')}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold tabular-nums text-emerald-600" data-font="display">{data?.delivered_orders || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Delivered</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold tabular-nums text-rose-600" data-font="display">{data?.cancelled_orders || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Cancelled</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold tabular-nums text-blue-600" data-font="display">{data?.pending_drivers || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Pending Drivers</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold tabular-nums text-purple-600" data-font="display">{data?.approved_drivers || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Approved Drivers</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
